<script lang="ts" setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import type { GoToItem } from '../../types';
import { compareItemPositionInToc } from '../../util/toc';
import useRenditionStore from '../../stores/rendition';
import type { EnhancedTocItem } from '../../types/navigation';
import useNavigationStore from '../../stores/navigation';
import useUiStore from '../../stores/ui';
import useReaderStore from '../../stores/reader';
import useNavigationTriggerStore from '../../stores/navigation-trigger';

type Emits = { (e: 'goToContent', item: GoToItem): void };
const emit = defineEmits<Emits>();

const { setNavigationTrigger } = useNavigationTriggerStore();

type Props = { item: EnhancedTocItem & { level: number } };
const props = defineProps<Props>();

const { enhancedToc, currentTocItem } = storeToRefs(useNavigationStore());
const { isItemShownOnPage } = useRenditionStore();
const { toggleTableOfContentDrawer } = useUiStore();
const { isAudioOnly } = storeToRefs(useReaderStore());
const { smAndDown } = useDisplay();

/**
 * Check if this ToC item is active
 * Uses currentTocItem from position tracking as single source of truth
 */
const isActive = computed(() => {
  // ONLY use currentTocItem from position tracking (no fallback)
  if (!currentTocItem.value) return false;

  // MUST match by id (with fragment), NOT by href (without fragment)
  // All ToC items on same page have same href, but different ids
  if (props.item.id && currentTocItem.value.id && props.item.id === currentTocItem.value.id) {
    return true;
  }

  // If no id, fall back to exact href match (rare case)
  if (!props.item.id && !currentTocItem.value.id && currentTocItem.value.href === props.item.href) {
    return true;
  }

  return false;
});

const goToPageFromItem = async (item: EnhancedTocItem) => {
  const comparison = compareItemPositionInToc(enhancedToc.value, item.href, (i) => isItemShownOnPage(i));
  if (smAndDown.value) {
    toggleTableOfContentDrawer(false);
  }

  const targetItem = isAudioOnly.value ? item.href : item.cfi || item.href;

  setNavigationTrigger('TOC');
  emit('goToContent', {
    target: targetItem,
    isGoingForwardsInBook: comparison === 1,
  });
};
</script>

<template>
  <div class="toc-item-group">
    <v-list-item
      class="text-left pe-2"
      :class="`ps-${2 + item.level * 2}`"
      :density="item.level ? 'compact' : undefined"
      link
      color="primary"
      :active="isActive"
      :data-testid="`bp-toc-item-${item.id}`"
      @click.stop="goToPageFromItem(item)"
    >
      <span class="label">{{ item.label }}</span>
    </v-list-item>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-list-item) {
  min-height: unset;
}
.item .v-list-item {
  min-height: unset;
}
</style>
